/**
 * src/pages/venue/PublishQueuePage.tsx — 2004va
 *
 * Two sections:
 *   - Queued to post: shots marked 'posted' or 'overridden', not yet
 *     actually published to a channel. This is the demo endpoint for the
 *     publish step — in production a separate publish runner sends these
 *     to the venue's social channels.
 *   - Saved for later: shots marked 'saved' — held pending picker decision
 *
 * Future: add per-channel target (Instagram, Twitter, TikTok), scheduling,
 * caption drafting. For this demo the state-of-being-queued is the
 * endpoint itself.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Send, Save } from 'lucide-react'
import { fetchDigestPicks } from '@/lib/venue'
import { Spinner } from '@/components/ui'
import type { Venue, VenuePick } from '@/types'
import type { PickerSession } from '@/lib/venue'

interface Props { venue: Venue; picker: PickerSession }

export function PublishQueuePage({ venue }: Props) {
  const { data: posted, isLoading: postedLoading } = useQuery({
    queryKey: ['venue-publish-queue', venue.id],
    queryFn:  () => fetchDigestPicks({ venue_id: venue.id, limit: 100, action_filter: 'posted' }),
    staleTime: 5 * 60_000,
  })

  const { data: saved, isLoading: savedLoading } = useQuery({
    queryKey: ['venue-saved', venue.id],
    queryFn:  () => fetchDigestPicks({ venue_id: venue.id, limit: 100, action_filter: 'saved' }),
  })


  // Build direct URL map — Cloudinary URLs are public, no signing needed
  const signedMap = useMemo(() => {
    const picks = [...(posted ?? []), ...(saved ?? [])]
    const map: Record<string, string> = {}
    picks.forEach(p => {
      const url = p.media?.cloudinary_url
      if (url) map[p.media_item_id] = url.replace('/upload/', '/upload/w_400,c_fill,q_auto,f_auto/')
    })
    return map
  }, [posted, saved])

  const loading = postedLoading || savedLoading

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={24} />
      </div>
    )
  }

  const postedCount = posted?.length ?? 0
  const savedCount  = saved?.length  ?? 0

  if (postedCount === 0 && savedCount === 0) {
    return (
      <div className="bg-room-surface border border-room-border rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-2xl bg-room-surface border border-room-border flex items-center justify-center mx-auto mb-3">
          <Send size={22} className="text-room-muted" />
        </div>
        <p className="text-white font-semibold mb-1">Nothing queued yet</p>
        <p className="text-sm text-room-muted">
          Shots you choose to post or save will appear here.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {postedCount > 0 && (
        <Section
          title="Queued to post"
          subtitle={`${postedCount} shot${postedCount === 1 ? '' : 's'}`}
          picks={posted ?? []}
          signedMap={signedMap ?? {}}
          icon={Send}
          tone="accent"
        />
      )}

      {savedCount > 0 && (
        <Section
          title="Saved for later"
          subtitle={`${savedCount} shot${savedCount === 1 ? '' : 's'}`}
          picks={saved ?? []}
          signedMap={signedMap ?? {}}
          icon={Save}
          tone="muted"
        />
      )}
    </div>
  )
}

// ── Section ───────────────────────────────────────────────────────────────────

interface SectionProps {
  title:     string
  subtitle:  string
  picks:     VenuePick[]
  signedMap: Record<string, string>
  icon:      typeof Send
  tone:      'accent' | 'muted'
}

function Section({ title, subtitle, picks, signedMap, icon: Icon, tone }: SectionProps) {
  const iconTone = tone === 'accent' ? 'text-room-accent' : 'text-room-muted'
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className={iconTone} />
        <div>
          <h2 className="text-sm font-bold text-white">{title}</h2>
          <p className="text-[10px] uppercase tracking-widest text-room-muted font-semibold">{subtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {picks.map(pick => (
          <div
            key={pick.id}
            className="relative aspect-[3/4] bg-room-surface border border-room-border rounded-xl overflow-hidden"
          >
            {signedMap[pick.media_item_id] ? (
              <img
                src={signedMap[pick.media_item_id]}
                alt={pick.media?.ai_description ?? 'shot'}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-room-border to-room-bg" />
            )}
            <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
              <p className="text-[10px] text-white/80 truncate">
                {pick.media?.ai_tags?.slice(0, 2).join(' · ') ?? ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
